import { CuiButton } from 'front-lib'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'

import { FF_NEW_RESURRECTION_FLOW } from 'src/constants/feature-flags'
import { useExperiment } from 'src/hooks/useExperiment'
import { useIsMobile } from 'src/hooks/useIsMobile'
import { resetViewToLazyLoader } from 'src/redux-api-bridge/orders-swimlanes/actions'
import { reactivateSubscriptionInit } from 'src/shared/actions/reactivation'
import tracking from 'src/shared/tracking'
import { showReactivationAndOrderSuccessMessage } from 'src/utils/resurrection-utils'
import { trackNewResurrectionFlowEvent } from 'src/utils/tracking'
import { getCurrentDate } from '../../../../selectors'

import './OrderSummaryGutter.scss'

export const OrderSummaryGutter = ({ onConfirm, onViewSelection }) => {
  const { t } = useTranslation()
  const history = useHistory()
  const buttonClasses = 'large'
  const currentDate = useSelector(getCurrentDate)
  const dispatch = useDispatch()
  const {
    active: isNewResurrectionFlowEnabled
  } = useExperiment(FF_NEW_RESURRECTION_FLOW, { trackOnMount: true })

  const [isProcessing, setIsProcessing] = useState(false)
  const [reactivationAttempted, setReactivationAttempted] = useState(false)
  const [orderPlacementAttempted, setOrderPlacementAttempted] = useState(false)

  // Get reactivation state from Redux
  const reactivationState = useSelector(state => state.reactivation || {})

  useEffect(() => {
    const reactivationStatus = reactivationState.status || 'idle'
    const reactivationError = reactivationState.error || null
    const reactivationPayload = reactivationState.payload || {}

    console.log('[ORDER_GUTTER_DEBUG] useEffect triggered with:', {
      reactivationAttempted,
      orderPlacementAttempted,
      isNewResurrectionFlowEnabled,
      reactivationStatus,
      reactivationPayload
    })

    if (reactivationAttempted && isNewResurrectionFlowEnabled) {
      if (reactivationStatus === 'success') {
        const isOrganic = reactivationPayload?.isOrganic

        console.log(
          '[ORDER_GUTTER_DEBUG] Reactivation succeeded, isOrganic:',
          isOrganic
        )

        // For organic users, we need to place the order after reactivation
        if (isOrganic && !orderPlacementAttempted) {
          console.log(
            '[ORDER_GUTTER_DEBUG] Starting order placement for organic user'
          )
          // Now that reactivation is complete and subscription state is reloaded,
          // we can safely place the order
          setOrderPlacementAttempted(true)

          // Create a custom onConfirm that doesn't redirect
          const customOnConfirm = () => {
            console.log(
              '[ORDER_GUTTER_DEBUG] Executing custom onConfirm for organic user'
            )
            // Call the original onConfirm but prevent its navigation
            const originalPush = history.push
            const originalReplace = history.replace

            console.log(
              '[ORDER_GUTTER_DEBUG] Temporarily overriding history methods'
            )
            // Temporarily override history methods to prevent navigation
            history.push = () => {
              console.log(
                '[ORDER_GUTTER_DEBUG] Blocked history.push call during order placement'
              )
            }
            history.replace = () => {
              console.log(
                '[ORDER_GUTTER_DEBUG] Blocked history.replace call during order placement'
              )
            }

            try {
              console.log('[ORDER_GUTTER_DEBUG] Calling original onConfirm()')
              onConfirm()

              console.log(
                '[ORDER_GUTTER_DEBUG] Order placement successful, showing success message'
              )
              // Show success message and navigate manually
              showReactivationAndOrderSuccessMessage()

              // Small delay to ensure order state is updated before navigation
              setTimeout(() => {
                console.log(
                  '[ORDER_GUTTER_DEBUG] Restoring history methods and navigating to root'
                )
                // Restore original history methods
                history.push = originalPush
                history.replace = originalReplace

                // Navigate to root
                history.push('/')

                setIsProcessing(false)
                setReactivationAttempted(false)
                setOrderPlacementAttempted(false)
              }, 100)
            } catch (error) {
              // Restore history methods on error
              console.error(
                '[ORDER_GUTTER_DEBUG] Error during order placement:',
                error
              )
              history.push = originalPush
              history.replace = originalReplace

              console.error('Error placing order:', error)
              toast.error(t('gutter.order_placement_failed'))
              setIsProcessing(false)
              setReactivationAttempted(false)
              setOrderPlacementAttempted(false)
            }
          }

          customOnConfirm()
          return
        }

        // For non-organic users, show toast and redirect
        if (!isOrganic) {
          console.log(
            '[ORDER_GUTTER_DEBUG] Non-organic user, showing success message and redirecting'
          )
          showReactivationAndOrderSuccessMessage()
          // Redirect to root instead of /orders
          history.push('/')
          setIsProcessing(false)
          setReactivationAttempted(false)
        }
      } else if (reactivationStatus === 'failure') {
        console.error(
          '[ORDER_GUTTER_DEBUG] Reactivation failed:',
          reactivationError
        )
        toast.error(reactivationError || t('gutter.reactivation_failed_toast'))
        setIsProcessing(false)
        setReactivationAttempted(false)
        setOrderPlacementAttempted(false)
      } else if (reactivationStatus === 'pending') {
        console.log(
          '[ORDER_GUTTER_DEBUG] Reactivation pending, setting processing state'
        )
        setIsProcessing(true)
      }
    }
  }, [
    reactivationState,
    reactivationAttempted,
    orderPlacementAttempted,
    isNewResurrectionFlowEnabled,
    onConfirm,
    history,
    dispatch,
    currentDate,
    t
  ])

  const onViewSelectionClick = () => {
    tracking.track('CTA Button Clicked', {
      cta_text: ' View Selection'
    })
    onViewSelection()
  }

  const handleOnConfirm = () => {
    if (isNewResurrectionFlowEnabled) {
      console.log(
        '[ORDER_GUTTER_DEBUG] Starting new resurrection flow - dispatching reactivateSubscriptionInit'
      )
      setIsProcessing(true)
      setReactivationAttempted(true)
      setOrderPlacementAttempted(false)
      trackNewResurrectionFlowEvent(
        'OrderSummaryGutter',
        'confirm_order_and_resubscribe_clicked'
      )
      dispatch(reactivateSubscriptionInit({ history }))
    } else {
      console.log(
        '[ORDER_GUTTER_DEBUG] Using legacy flow - calling onConfirm directly'
      )
      onConfirm()
      dispatch(
        resetViewToLazyLoader({
          date: currentDate.date
        })
      )
    }
  }

  const buttonText = useMemo(() => {
    if (isProcessing && isNewResurrectionFlowEnabled) {
      return t('gutter.confirming_order_and_resubscribing')
    }
    return isNewResurrectionFlowEnabled
      ? t('gutter.confirm_order_and_resubscribe')
      : t('gutter.confirm_order')
  }, [isProcessing, isNewResurrectionFlowEnabled, t])

  return (
    <div className="cui-gutter-v2-container">
      <div className="cui-gutter-v2-content">
        <CuiButton
          disabled={currentDate?.order?.id === '0' || isProcessing}
          onClick={handleOnConfirm}
          size={buttonClasses}
          fill="solid"
          className={useIsMobile() ? 'full-width' : ''}>
          {buttonText}
        </CuiButton>
        {useIsMobile() && (
          <button
            type="button"
            className="view-selection-label"
            onClick={onViewSelectionClick}
            aria-label={t('gutter.view_selection')}
            title={t('gutter.view_selection')}>
            {t('gutter.view_selection')}
          </button>
        )}
      </div>
    </div>
  )
}
